// Types para Process Mining do Protocolo de Dor Hospitalar

// ============================================================================
// EVENT LOG STRUCTURES
// ============================================================================

export interface EventLog {
  case_id: string;                    // ID único do caso (paciente)
  activity: ActivityType;             // Atividade executada
  timestamp: Date;                    // Momento da execução
  resource: string;                   // Quem executou (médico, enfermeiro)
  lifecycle: LifecycleTransition;     // start, complete, abort, suspend
  attributes: EventAttributes;        // Contexto adicional
  event_id: string;                   // ID único do evento
  organization: string;               // Organização/Hospital
  process_instance: string;           // Instância específica do processo
}

export interface EventAttributes {
  // Dados do paciente
  patient_id: string;
  patient_age: number;
  gender: 'M' | 'F' | 'O';
  admission_type: 'emergency' | 'elective' | 'transfer' | 'outpatient';
  admission_datetime: Date;
  
  // Contexto clínico
  pain_level_before: number;          // 0-10
  pain_level_after?: number;          // 0-10
  pain_type: PainType;
  pain_location?: string[];           // Localização da dor
  pain_characteristics?: string[];    // Características (queimação, latejante, etc.)
  comorbidities: string[];
  current_medications?: string[];
  allergies?: string[];
  
  // Contexto organizacional
  department: string;
  unit: string;
  room_number?: string;
  shift: 'morning' | 'afternoon' | 'night' | 'weekend';
  bed_occupancy_rate: number;         // 0-1
  staff_workload_index: number;       // Índice de carga de trabalho
  
  // Recursos e medicamentos
  medication_used?: string;
  dosage?: string;
  administration_route?: 'oral' | 'iv' | 'im' | 'topical' | 'other';
  equipment_used?: string[];
  procedure_performed?: string;
  
  // Outcomes e qualidade
  patient_satisfaction_score?: number; // 1-10
  adverse_events?: AdverseEvent[];
  protocol_compliance_score: number;   // 0-1
  pain_relief_achieved?: boolean;
  time_to_pain_relief?: number;        // em minutos
  
  // Contexto temporal
  day_of_week: number;                // 0-6 (Sunday-Saturday)
  hour_of_day: number;               // 0-23
  is_holiday?: boolean;
  is_weekend?: boolean;
  
  // Custo e recursos
  estimated_cost?: number;
  resource_consumption?: ResourceConsumption;
  
  // Qualidade e conformidade
  documentation_completeness: number; // 0-1
  guideline_adherence_score: number; // 0-1
  response_time_sla_met: boolean;
}

export interface AdverseEvent {
  event_type: string;
  severity: 'mild' | 'moderate' | 'severe' | 'life_threatening';
  description: string;
  timestamp: Date;
  resolved: boolean;
  root_cause?: string;
}

export interface ResourceConsumption {
  nurse_time_minutes: number;
  physician_time_minutes: number;
  medication_cost: number;
  equipment_usage_cost: number;
  total_estimated_cost: number;
}

// ============================================================================
// ACTIVITY TYPES
// ============================================================================

export enum ActivityType {
  // Atividades de Avaliação
  'pain_assessment_initial' = 'Avaliação Inicial da Dor',
  'pain_reassessment' = 'Reavaliação da Dor',
  'pain_scale_application' = 'Aplicação Escala de Dor',
  'comprehensive_pain_assessment' = 'Avaliação Abrangente da Dor',
  'functional_assessment' = 'Avaliação Funcional',
  
  // Atividades de Intervenção Farmacológica
  'medication_administration' = 'Administração de Medicamento',
  'medication_adjustment' = 'Ajuste de Medicação',
  'pain_medication_switch' = 'Troca de Analgésico',
  'rescue_medication' = 'Medicação de Resgate',
  'medication_discontinuation' = 'Suspensão de Medicação',
  
  // Atividades de Intervenção Não-Farmacológica
  'physical_therapy' = 'Fisioterapia',
  'relaxation_technique' = 'Técnica de Relaxamento',
  'distraction_therapy' = 'Terapia de Distração',
  'heat_cold_therapy' = 'Termoterapia/Crioterapia',
  'massage_therapy' = 'Massoterapia',
  'acupuncture' = 'Acupuntura',
  
  // Atividades de Protocolo
  'pain_protocol_initiation' = 'Início do Protocolo',
  'protocol_modification' = 'Modificação do Protocolo',
  'protocol_escalation' = 'Escalation do Protocolo',
  'protocol_completion' = 'Conclusão do Protocolo',
  
  // Atividades de Monitoramento
  'pain_monitoring' = 'Monitoramento da Dor',
  'response_evaluation' = 'Avaliação da Resposta',
  'side_effects_monitoring' = 'Monitoramento de Efeitos Colaterais',
  'vital_signs_check' = 'Verificação Sinais Vitais',
  'functional_status_check' = 'Verificação Status Funcional',
  
  // Atividades de Documentação
  'pain_documentation' = 'Documentação da Dor',
  'nursing_notes' = 'Anotações de Enfermagem',
  'physician_notes' = 'Anotações Médicas',
  'handoff_communication' = 'Comunicação de Passagem',
  'family_education' = 'Educação da Família',
  
  // Atividades de Coordenação
  'multidisciplinary_consultation' = 'Consulta Multidisciplinar',
  'specialist_referral' = 'Encaminhamento Especialista',
  'case_discussion' = 'Discussão de Caso',
  
  // Atividades de Alta
  'discharge_planning' = 'Planejamento de Alta',
  'discharge_education' = 'Orientação de Alta',
  'follow_up_scheduling' = 'Agendamento Follow-up',
  
  // Atividades de Qualidade
  'quality_audit' = 'Auditoria de Qualidade',
  'protocol_compliance_check' = 'Verificação de Conformidade',
  'outcome_measurement' = 'Mensuração de Resultados',
  'patient_satisfaction_survey' = 'Pesquisa Satisfação Paciente',
  'incident_reporting' = 'Notificação de Incidente'
}

export enum PainType {
  'acute' = 'Aguda',
  'chronic' = 'Crônica',
  'cancer_related' = 'Relacionada ao Câncer',
  'procedural' = 'Procedural',
  'postoperative' = 'Pós-operatória',
  'neuropathic' = 'Neuropática',
  'inflammatory' = 'Inflamatória',
  'mixed' = 'Mista'
}

export enum LifecycleTransition {
  'start' = 'start',
  'complete' = 'complete',
  'abort' = 'abort',
  'suspend' = 'suspend',
  'resume' = 'resume',
  'assign' = 'assign',
  'withdraw' = 'withdraw'
}

// ============================================================================
// PROCESS MINING ANALYSIS
// ============================================================================

export interface ProcessModel {
  id: string;
  name: string;
  algorithm_used: ProcessDiscoveryAlgorithm;
  creation_timestamp: Date;
  data_source: EventLogSummary;
  model_representation: ProcessModelRepresentation;
  quality_metrics: ProcessModelQuality;
  activities: ProcessActivity[];
  transitions: ProcessTransition[];
  variants: ProcessVariant[];
}

export interface ProcessActivity {
  id: string;
  name: ActivityType;
  frequency: number;
  average_duration: number;
  median_duration: number;
  std_deviation: number;
  resources_involved: string[];
  costs: ActivityCosts;
  quality_indicators: ActivityQualityIndicators;
}

export interface ProcessTransition {
  from_activity: string;
  to_activity: string;
  frequency: number;
  probability: number;
  average_waiting_time: number;
  resources_involved: string[];
}

export interface ProcessVariant {
  id: string;
  sequence: ActivityType[];
  frequency: number;
  percentage: number;
  average_duration: number;
  average_cost: number;
  quality_score: number;
  patient_characteristics: VariantCharacteristics;
}

export interface VariantCharacteristics {
  age_range: { min: number; max: number };
  gender_distribution: { M: number; F: number; O: number };
  pain_types: { [key in PainType]?: number };
  departments: { [department: string]: number };
  complexity_score: number;
}

export enum ProcessDiscoveryAlgorithm {
  'alpha_miner' = 'Alpha Miner',
  'inductive_miner' = 'Inductive Miner',
  'heuristics_miner' = 'Heuristics Miner',
  'fuzzy_miner' = 'Fuzzy Miner',
  'split_miner' = 'Split Miner'
}

export interface ProcessModelQuality {
  fitness: number;           // 0-1
  precision: number;         // 0-1
  generalization: number;    // 0-1
  simplicity: number;        // 0-1
  f1_score: number;         // 0-1
  complexity_score: number;  // Número de elementos no modelo
}

export interface EventLogSummary {
  total_cases: number;
  total_events: number;
  unique_activities: number;
  time_range: { start: Date; end: Date };
  departments_covered: string[];
  patient_demographics: PatientDemographicsSummary;
}

export interface PatientDemographicsSummary {
  age_distribution: { [age_group: string]: number };
  gender_distribution: { M: number; F: number; O: number };
  pain_type_distribution: { [key in PainType]?: number };
  admission_type_distribution: { [type: string]: number };
}

// ============================================================================
// CONFORMANCE CHECKING
// ============================================================================

export interface ConformanceResult {
  case_id: string;
  fitness_score: number;        // 0-1
  deviations: Deviation[];
  aligned_trace: AlignedActivity[];
  cost: number;
  conformance_status: ConformanceStatus;
  rule_violations: RuleViolation[];
}

export interface Deviation {
  type: DeviationType;
  position: number;
  expected_activity?: ActivityType;
  actual_activity?: ActivityType;
  severity: ViolationSeverity;
  description: string;
  impact_score: number;
  timestamp?: Date;
}

export enum DeviationType {
  'missing_activity' = 'Atividade Ausente',
  'extra_activity' = 'Atividade Extra',
  'wrong_activity' = 'Atividade Incorreta',
  'timing_violation' = 'Violação de Timing',
  'resource_violation' = 'Violação de Recurso',
  'sequence_violation' = 'Violação de Sequência'
}

export enum ConformanceStatus {
  'compliant' = 'Conforme',
  'minor_deviation' = 'Desvio Menor',
  'major_deviation' = 'Desvio Maior',
  'non_compliant' = 'Não Conforme'
}

export interface AlignedActivity {
  log_activity?: ActivityType;
  model_activity?: ActivityType;
  alignment_type: AlignmentType;
  cost: number;
  timestamp?: Date;
}

export enum AlignmentType {
  'match' = 'Correspondência',
  'log_move' = 'Movimento do Log',
  'model_move' = 'Movimento do Modelo',
  'invisible' = 'Invisível'
}

export interface ComplianceRule {
  id: string;
  name: string;
  category: RuleCategory;
  type: RuleType;
  description: string;
  condition: string;
  severity: ViolationSeverity;
  active: boolean;
  created_date: Date;
  examples: {
    compliant: string[];
    non_compliant: string[];
  };
  parameters?: RuleParameters;
}

export enum RuleCategory {
  'clinical_safety' = 'Segurança Clínica',
  'operational_efficiency' = 'Eficiência Operacional',
  'quality_assurance' = 'Garantia de Qualidade',
  'regulatory_compliance' = 'Conformidade Regulatória',
  'protocol_adherence' = 'Aderência ao Protocolo'
}

export enum RuleType {
  'temporal' = 'Temporal',
  'sequential' = 'Sequencial',
  'cardinality' = 'Cardinalidade',
  'resource' = 'Recurso',
  'data' = 'Dados',
  'performance' = 'Performance'
}

export enum ViolationSeverity {
  'low' = 'Baixa',
  'medium' = 'Média',
  'high' = 'Alta',
  'critical' = 'Crítica'
}

export interface RuleParameters {
  [key: string]: any;
  time_threshold?: number;
  count_threshold?: number;
  percentage_threshold?: number;
  resource_constraints?: string[];
}

export interface RuleViolation {
  rule_id: string;
  rule_name: string;
  violation_type: DeviationType;
  severity: ViolationSeverity;
  description: string;
  timestamp: Date;
  case_id: string;
  activity_involved?: ActivityType;
  expected_value?: any;
  actual_value?: any;
  impact_assessment: ImpactAssessment;
}

export interface ImpactAssessment {
  patient_safety_risk: 'low' | 'medium' | 'high' | 'critical';
  operational_impact: 'minimal' | 'moderate' | 'significant' | 'severe';
  cost_implication: number;
  quality_impact: number;
  recommended_action: string;
}

// ============================================================================
// PERFORMANCE METRICS
// ============================================================================

export interface PerformanceMetrics {
  temporal_metrics: TemporalMetrics;
  resource_metrics: ResourceMetrics;
  quality_metrics: QualityMetrics;
  cost_metrics: CostMetrics;
  efficiency_metrics: EfficiencyMetrics;
}

export interface TemporalMetrics {
  cycle_time: TimeMetric;
  waiting_time: TimeMetric;
  processing_time: TimeMetric;
  response_time: TimeMetric;
  throughput: ThroughputMetric;
  bottleneck_analysis: BottleneckAnalysis;
}

export interface TimeMetric {
  mean: number;
  median: number;
  std_deviation: number;
  min: number;
  max: number;
  percentile_95: number;
  percentile_99: number;
  trend: TrendDirection;
  sla_compliance: number; // percentage
}

export enum TrendDirection {
  'improving' = 'Melhorando',
  'stable' = 'Estável',
  'deteriorating' = 'Piorando'
}

export interface ThroughputMetric {
  cases_per_hour: number;
  cases_per_day: number;
  cases_per_week: number;
  capacity_utilization: number; // 0-1
  demand_vs_capacity: number;
}

export interface BottleneckAnalysis {
  bottleneck_activities: BottleneckActivity[];
  resource_bottlenecks: ResourceBottleneck[];
  time_based_bottlenecks: TimeBasedBottleneck[];
}

export interface BottleneckActivity {
  activity: ActivityType;
  waiting_time_contribution: number; // percentage
  frequency: number;
  average_waiting_time: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export interface ResourceBottleneck {
  resource_type: string;
  utilization_rate: number;
  demand_vs_capacity: number;
  impact_on_cycle_time: number;
  affected_activities: ActivityType[];
}

export interface TimeBasedBottleneck {
  time_period: string; // e.g., "Monday 14:00-16:00"
  intensity: number;
  affected_processes: number;
  average_delay: number;
}

export interface ResourceMetrics {
  utilization: ResourceUtilization;
  productivity: ResourceProductivity;
  workload_distribution: WorkloadDistribution;
  collaboration_metrics: CollaborationMetrics;
}

export interface ResourceUtilization {
  by_role: { [role: string]: number };
  by_individual: { [resource: string]: number };
  by_shift: { [shift: string]: number };
  by_department: { [department: string]: number };
  optimal_ranges: { [resource: string]: { min: number; max: number } };
}

export interface ResourceProductivity {
  activities_per_hour: { [resource: string]: number };
  cases_completed: { [resource: string]: number };
  quality_score: { [resource: string]: number };
  efficiency_index: { [resource: string]: number };
}

export interface WorkloadDistribution {
  gini_coefficient: number; // 0-1, measure of inequality
  workload_variance: number;
  overloaded_resources: string[];
  underutilized_resources: string[];
  balancing_opportunities: BalancingOpportunity[];
}

export interface BalancingOpportunity {
  from_resource: string;
  to_resource: string;
  activities_to_transfer: ActivityType[];
  expected_improvement: number;
  feasibility_score: number;
}

export interface CollaborationMetrics {
  handoff_efficiency: number; // 0-1
  communication_quality: number; // 0-1
  team_coordination_index: number; // 0-1
  cross_functional_activities: number;
  collaboration_network: CollaborationNetwork;
}

export interface CollaborationNetwork {
  nodes: NetworkNode[];
  edges: NetworkEdge[];
  centrality_measures: { [resource: string]: CentralityMeasures };
}

export interface NetworkNode {
  id: string;
  type: 'person' | 'department' | 'role';
  attributes: { [key: string]: any };
}

export interface NetworkEdge {
  from: string;
  to: string;
  weight: number;
  interaction_type: 'handoff' | 'consultation' | 'collaboration';
}

export interface CentralityMeasures {
  degree_centrality: number;
  betweenness_centrality: number;
  closeness_centrality: number;
  eigenvector_centrality: number;
}

export interface QualityMetrics {
  patient_outcomes: PatientOutcomeMetrics;
  safety_metrics: SafetyMetrics;
  satisfaction_metrics: SatisfactionMetrics;
  clinical_effectiveness: ClinicalEffectivenessMetrics;
}

export interface PatientOutcomeMetrics {
  pain_reduction_rate: number; // 0-1
  treatment_success_rate: number; // 0-1
  time_to_pain_relief: TimeMetric;
  functional_improvement: number; // 0-1
  readmission_rate: number; // 0-1
  complications_rate: number; // 0-1
}

export interface SafetyMetrics {
  medication_errors: number;
  adverse_events: number;
  near_misses: number;
  safety_incidents: SafetyIncident[];
  error_rate_by_activity: { [activity: string]: number };
  preventable_events: number;
}

export interface SafetyIncident {
  id: string;
  type: string;
  severity: ViolationSeverity;
  description: string;
  root_cause: string;
  corrective_actions: string[];
  timestamp: Date;
  resolved: boolean;
}

export interface SatisfactionMetrics {
  patient_satisfaction: number; // 1-10
  family_satisfaction: number; // 1-10
  staff_satisfaction: number; // 1-10
  satisfaction_by_department: { [department: string]: number };
  satisfaction_trends: TrendData[];
}

export interface ClinicalEffectivenessMetrics {
  protocol_adherence_rate: number; // 0-1
  guideline_compliance: number; // 0-1
  evidence_based_practice_score: number; // 0-1
  clinical_decision_accuracy: number; // 0-1
  outcome_prediction_accuracy: number; // 0-1
}

export interface CostMetrics {
  total_cost_per_case: CostBreakdown;
  cost_efficiency: CostEfficiencyMetrics;
  waste_identification: WasteMetrics;
  roi_analysis: ROIAnalysis;
}

export interface CostBreakdown {
  medication_cost: number;
  staff_cost: number;
  equipment_cost: number;
  facility_cost: number;
  administrative_cost: number;
  total_cost: number;
}

export interface CostEfficiencyMetrics {
  cost_per_outcome: number;
  cost_variance: number;
  benchmark_comparison: number;
  efficiency_opportunities: AutomationOpportunity[];
}

export interface WasteMetrics {
  time_waste: number; // em horas
  resource_waste: number; // em currency
  process_waste: WasteCategory[];
  improvement_potential: number;
}

export interface WasteCategory {
  category: 'overproduction' | 'waiting' | 'transportation' | 'overprocessing' | 'inventory' | 'motion' | 'defects';
  amount: number;
  cost_impact: number;
  improvement_actions: string[];
}

export interface ROIAnalysis {
  initial_investment: number;
  operational_savings: number;
  quality_improvements_value: number;
  total_benefits: number;
  net_present_value: number;
  payback_period: number; // em meses
  roi_percentage: number;
}

export interface EfficiencyMetrics {
  overall_equipment_effectiveness: number; // 0-1
  first_time_right_rate: number; // 0-1
  process_efficiency_index: number; // 0-1
  automation_opportunities: AutomationOpportunity[];
}

export interface AutomationOpportunity {
  activity: ActivityType;
  automation_potential: number; // 0-1
  expected_savings: number;
  implementation_effort: 'low' | 'medium' | 'high';
  roi_estimate: number;
}

// ============================================================================
// PREDICTIVE ANALYTICS
// ============================================================================

export interface PredictiveModel {
  id: string;
  name: string;
  type: ModelType;
  target_variable: string;
  algorithm: MLAlgorithm;
  features: ModelFeature[];
  performance_metrics: ModelPerformance;
  training_data: ModelTrainingData;
  deployment_status: DeploymentStatus;
  created_date: Date;
  last_updated: Date;
}

export enum ModelType {
  'outcome_prediction' = 'Predição de Desfecho',
  'demand_forecasting' = 'Previsão de Demanda',
  'anomaly_detection' = 'Detecção de Anomalia',
  'resource_optimization' = 'Otimização de Recursos',
  'risk_prediction' = 'Predição de Risco'
}

export enum MLAlgorithm {
  'random_forest' = 'Random Forest',
  'gradient_boosting' = 'Gradient Boosting',
  'neural_network' = 'Rede Neural',
  'svm' = 'Support Vector Machine',
  'logistic_regression' = 'Regressão Logística',
  'time_series' = 'Série Temporal',
  'clustering' = 'Clustering',
  'ensemble' = 'Ensemble'
}

export interface ModelFeature {
  name: string;
  type: 'numerical' | 'categorical' | 'boolean' | 'datetime';
  importance: number; // 0-1
  description: string;
  correlation_with_target: number; // -1 to 1
}

export interface ModelPerformance {
  accuracy?: number; // 0-1, for classification
  precision?: number; // 0-1, for classification
  recall?: number; // 0-1, for classification
  f1_score?: number; // 0-1, for classification
  rmse?: number; // for regression
  mae?: number; // for regression
  r_squared?: number; // for regression
  auc_roc?: number; // 0-1, for classification
  cross_validation_score: number;
}

export interface ModelTrainingData {
  total_samples: number;
  training_samples: number;
  validation_samples: number;
  test_samples: number;
  data_quality_score: number; // 0-1
  feature_engineering_applied: string[];
}

export enum DeploymentStatus {
  'development' = 'Em Desenvolvimento',
  'testing' = 'Em Teste',
  'staging' = 'Em Homologação',
  'production' = 'Em Produção',
  'deprecated' = 'Descontinuado'
}

export interface Prediction {
  id: string;
  model_id: string;
  case_id: string;
  prediction_value: any;
  confidence_score: number; // 0-1
  explanation: PredictionExplanation;
  timestamp: Date;
  actual_value?: any; // for validation
  accuracy?: number; // for validation
}

export interface PredictionExplanation {
  feature_importance: { [feature: string]: number };
  shap_values?: { [feature: string]: number };
  decision_path?: DecisionNode[];
  natural_language_explanation: string;
}

export interface DecisionNode {
  feature: string;
  condition: string;
  value: any;
  contribution: number;
}

// ============================================================================
// RECOMMENDATION SYSTEM
// ============================================================================

export interface Recommendation {
  id: string;
  type: RecommendationType;
  priority: RecommendationPriority;
  title: string;
  description: string;
  rationale: string;
  confidence_score: number; // 0-1
  expected_impact: ExpectedImpact;
  implementation_effort: ImplementationEffort;
  target_audience: string[];
  created_date: Date;
  status: RecommendationStatus;
  feedback?: RecommendationFeedback;
}

export enum RecommendationType {
  'next_best_action' = 'Próxima Melhor Ação',
  'resource_allocation' = 'Alocação de Recursos',
  'process_improvement' = 'Melhoria de Processo',
  'protocol_adjustment' = 'Ajuste de Protocolo',
  'risk_mitigation' = 'Mitigação de Risco',
  'efficiency_optimization' = 'Otimização de Eficiência'
}

export enum RecommendationPriority {
  'low' = 'Baixa',
  'medium' = 'Média',
  'high' = 'Alta',
  'urgent' = 'Urgente'
}

export interface ExpectedImpact {
  quality_improvement: number; // 0-1
  efficiency_gain: number; // 0-1
  cost_reduction: number; // monetary value
  patient_satisfaction_increase: number; // 0-1
  risk_reduction: number; // 0-1
}

export enum ImplementationEffort {
  'minimal' = 'Mínimo',
  'low' = 'Baixo',
  'medium' = 'Médio',
  'high' = 'Alto',
  'very_high' = 'Muito Alto'
}

export enum RecommendationStatus {
  'pending' = 'Pendente',
  'accepted' = 'Aceita',
  'rejected' = 'Rejeitada',
  'implementing' = 'Implementando',
  'completed' = 'Concluída',
  'monitoring' = 'Monitorando'
}

export interface RecommendationFeedback {
  user_rating: number; // 1-5
  implementation_success: boolean;
  actual_impact: ExpectedImpact;
  lessons_learned: string;
  additional_comments: string;
  feedback_date: Date;
}

// ============================================================================
// DASHBOARD AND VISUALIZATION
// ============================================================================

export interface ProcessMiningDashboard {
  id: string;
  name: string;
  type: DashboardType;
  widgets: DashboardWidget[];
  filters: DashboardFilter[];
  refresh_interval: number; // em segundos
  target_audience: string[];
  created_by: string;
  created_date: Date;
  last_modified: Date;
}

export enum DashboardType {
  'executive' = 'Executivo',
  'operational' = 'Operacional',
  'clinical' = 'Clínico',
  'quality' = 'Qualidade',
  'process_analysis' = 'Análise de Processo',
  'performance' = 'Performance'
}

export interface DashboardWidget {
  id: string;
  type: WidgetType;
  title: string;
  position: { x: number; y: number; width: number; height: number };
  configuration: WidgetConfiguration;
  data_source: string;
  refresh_rate: number;
}

export enum WidgetType {
  'kpi_card' = 'Cartão KPI',
  'line_chart' = 'Gráfico de Linha',
  'bar_chart' = 'Gráfico de Barras',
  'process_model' = 'Modelo de Processo',
  'heatmap' = 'Mapa de Calor',
  'scatter_plot' = 'Gráfico de Dispersão',
  'table' = 'Tabela',
  'gauge' = 'Medidor',
  'funnel' = 'Funil',
  'sankey' = 'Diagrama Sankey',
  'network' = 'Rede',
  'calendar' = 'Calendário',
  'alert_list' = 'Lista de Alertas'
}

export interface WidgetConfiguration {
  [key: string]: any;
  metrics?: string[];
  dimensions?: string[];
  time_period?: string;
  aggregation?: 'sum' | 'avg' | 'min' | 'max' | 'count';
  visualization_options?: any;
}

export interface DashboardFilter {
  id: string;
  name: string;
  type: FilterType;
  values: any[];
  default_value?: any;
  applies_to_widgets: string[];
}

export enum FilterType {
  'date_range' = 'Período',
  'single_select' = 'Seleção Única',
  'multi_select' = 'Seleção Múltipla',
  'numeric_range' = 'Intervalo Numérico',
  'text_search' = 'Busca Textual'
}

// ============================================================================
// ALERTS AND NOTIFICATIONS
// ============================================================================

export interface Alert {
  id: string;
  type: AlertType;
  priority: AlertPriority;
  title: string;
  description: string;
  source: AlertSource;
  timestamp: Date;
  status: AlertStatus;
  assignee?: string;
  escalation_level: number;
  related_entities: RelatedEntity[];
  actions_taken: AlertAction[];
  resolution?: AlertResolution;
}

export enum AlertType {
  'compliance_violation' = 'Violação de Conformidade',
  'performance_degradation' = 'Degradação de Performance',
  'anomaly_detected' = 'Anomalia Detectada',
  'sla_breach' = 'Violação de SLA',
  'resource_shortage' = 'Escassez de Recursos',
  'quality_issue' = 'Problema de Qualidade',
  'safety_concern' = 'Preocupação de Segurança',
  'system_issue' = 'Problema de Sistema'
}

export enum AlertPriority {
  'info' = 'Informativo',
  'low' = 'Baixa',
  'medium' = 'Média',
  'high' = 'Alta',
  'critical' = 'Crítica'
}

export interface AlertSource {
  system: string;
  component: string;
  rule_id?: string;
  model_id?: string;
}

export enum AlertStatus {
  'new' = 'Nova',
  'acknowledged' = 'Reconhecida',
  'in_progress' = 'Em Andamento',
  'resolved' = 'Resolvida',
  'false_positive' = 'Falso Positivo',
  'escalated' = 'Escalada'
}

export interface RelatedEntity {
  type: 'patient' | 'case' | 'resource' | 'activity' | 'process';
  id: string;
  name: string;
}

export interface AlertAction {
  timestamp: Date;
  user: string;
  action: string;
  description: string;
  result?: string;
}

export interface AlertResolution {
  resolved_by: string;
  resolution_timestamp: Date;
  resolution_type: 'fixed' | 'mitigated' | 'accepted_risk' | 'false_positive';
  description: string;
  preventive_measures: string[];
}

// ============================================================================
// TRENDS AND ANALYTICS
// ============================================================================

export interface TrendData {
  timestamp: Date;
  value: number;
  label?: string;
  metadata?: { [key: string]: any };
}

export interface TrendAnalysis {
  trend_direction: TrendDirection;
  slope: number;
  confidence_interval: { lower: number; upper: number };
  seasonality_detected: boolean;
  anomalies: TrendAnomaly[];
  forecast?: TrendData[];
}

export interface TrendAnomaly {
  timestamp: Date;
  expected_value: number;
  actual_value: number;
  deviation_score: number;
  anomaly_type: 'spike' | 'dip' | 'change_point' | 'seasonal_deviation';
  explanation?: string;
}

export interface BenchmarkComparison {
  metric_name: string;
  current_value: number;
  benchmark_value: number;
  percentile_rank: number; // 0-100
  gap: number;
  trend: TrendDirection;
  benchmark_source: string;
  last_updated: Date;
}

export interface ActivityCosts {
  direct_cost: number;
  indirect_cost: number;
  opportunity_cost: number;
  total_cost: number;
  cost_per_minute: number;
  cost_trend: TrendDirection;
}

export interface ActivityQualityIndicators {
  success_rate: number; // 0-1
  error_rate: number; // 0-1
  patient_satisfaction: number; // 0-10
  compliance_score: number; // 0-1
  efficiency_score: number; // 0-1
}

export interface ProcessModelRepresentation {
  format: 'petri_net' | 'bpmn' | 'process_tree' | 'directly_follows_graph';
  serialized_model: string;
  visualization_data: any;
  layout_information: any;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

export type ProcessMiningResult<T> = {
  success: boolean;
  data?: T;
  error?: string;
  metadata?: {
    execution_time: number;
    memory_usage: number;
    version: string;
  };
};

export type TimeRange = {
  start: Date;
  end: Date;
  timezone?: string;
};

export type FilterCriteria = {
  [key: string]: any;
  time_range?: TimeRange;
  departments?: string[];
  patient_types?: string[];
  activities?: ActivityType[];
  resources?: string[];
};

export type AggregationLevel = 'hour' | 'day' | 'week' | 'month' | 'quarter' | 'year';

export type DataQuality = {
  completeness: number; // 0-1
  accuracy: number; // 0-1
  consistency: number; // 0-1
  timeliness: number; // 0-1
  overall_score: number; // 0-1
  issues: DataQualityIssue[];
};

export interface DataQualityIssue {
  type: 'missing_data' | 'invalid_data' | 'inconsistent_data' | 'outdated_data';
  field: string;
  severity: 'low' | 'medium' | 'high';
  count: number;
  percentage: number;
  description: string;
  suggested_fix: string;
}
